/** Minimal DOM helpers. No framework: the hot path is a few hundred absolutely
 *  positioned tiles, and hand-written transform updates beat a diff here. */

type Attrs = Record<string, string | number | boolean | null | undefined>;

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K, attrs: Attrs = {}, ...children: Array<Node | string | null | undefined>
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v === null || v === undefined || v === false) continue;
    if (k === 'class') node.className = String(v);
    else if (k === 'text') node.textContent = String(v);
    else if (k === 'html') node.innerHTML = String(v);
    else if (k.startsWith('data-') || k.startsWith('aria-')) node.setAttribute(k, String(v));
    else if (k in node) (node as unknown as Record<string, unknown>)[k] = v;
    else node.setAttribute(k, String(v));
  }
  for (const c of children) {
    if (c === null || c === undefined) continue;
    node.append(typeof c === 'string' ? document.createTextNode(c) : c);
  }
  return node;
}

export const frag = (...nodes: Array<Node | string>): DocumentFragment => {
  const f = document.createDocumentFragment();
  for (const n of nodes) f.append(n);
  return f;
};

export function on<K extends keyof HTMLElementEventMap>(
  target: HTMLElement | Window | Document,
  type: K | string,
  handler: (ev: HTMLElementEventMap[K]) => void,
  options?: AddEventListenerOptions,
): () => void {
  target.addEventListener(type, handler as EventListener, options);
  return () => target.removeEventListener(type, handler as EventListener, options);
}

export const clear = (node: Element): void => { node.replaceChildren(); };

export const clamp = (v: number, lo: number, hi: number): number =>
  v < lo ? lo : v > hi ? hi : v;

/** Announce to screen readers without moving focus. */
let liveRegion: HTMLElement | null = null;
export function announce(text: string): void {
  if (!liveRegion) {
    liveRegion = el('div', { class: 'sr-only', 'aria-live': 'polite', 'aria-atomic': 'true' });
    document.body.append(liveRegion);
  }
  liveRegion.textContent = '';
  // A same-text update is ignored by some screen readers unless the node is
  // genuinely re-rendered, so the write is deferred a frame.
  requestAnimationFrame(() => { if (liveRegion) liveRegion.textContent = text; });
}

/**
 * Write to the DOM only when the value actually changes.
 *
 * Anything repainted on a timer must not rewrite nodes that did not change.
 * Replacing a text node between a tap's pointerdown and pointerup loses the
 * click entirely — which is how the call button came to need pressing twice.
 */
export function setText(node: Element, text: string): void {
  if (node.textContent !== text) node.textContent = text;
}

export function setAttr(node: Element, name: string, value: string): void {
  if (node.getAttribute(name) !== value) node.setAttribute(name, value);
}

export function setClass(node: Element, name: string, on: boolean): void {
  if (node.classList.contains(name) !== on) node.classList.toggle(name, on);
}

export function setStyle(node: HTMLElement, prop: string, value: string): void {
  if (node.style.getPropertyValue(prop) !== value) {
    node.style.setProperty(prop, value);
  }
}

export function setDisabled(node: HTMLButtonElement, disabled: boolean): void {
  if (node.disabled !== disabled) node.disabled = disabled;
}
